import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import './ImageScroller.scss';
import { getImageUrl } from '../utils/imageUtils';

function TestScrollInfiniteData({
    data,
    months,
    yearsIsVisible,
    monthsIsVisible,
    keywordsIsVisible,
    setYearsIsVisible,
    setMonthsIsVisible,
    setKeywordsIsVisible
}) {
    const [activeImage, setActiveImage] = useState('');
    const [activeIndex, setActiveIndex] = useState(0);
    const [duplicatedPictures, setDuplicatedPictures] = useState([]);
    const [nextMonth, setNextMonth] = useState(null);
    const [previousMonth, setPreviousMonth] = useState(null);
    const imageRefs = useRef([]);
    const lastScrollY = useRef(0);
    const containerRef = useRef(null);
    const dataRef = useRef(data);
    const activeImageRef = useRef('');
    const duplicatedPicturesRef = useRef([]);

    // Update refs when data or state changes
    useEffect(() => {
        dataRef.current = data;
        activeImageRef.current = activeImage;
        duplicatedPicturesRef.current = duplicatedPictures;
    }, [data, activeImage, duplicatedPictures]);

    // Find the next and previous months
    useEffect(() => {
        if (data && months) {
            const currentIndex = months.findIndex(month => month.sys.id === data.sys.id);
            if (currentIndex !== -1) {
                // Set next month if available
                if (currentIndex < months.length - 1) {
                    setNextMonth(months[currentIndex + 1]);
                }
                // Set previous month if available
                if (currentIndex > 0) {
                    setPreviousMonth(months[currentIndex - 1]);
                }
            }
        }
    }, [data, months]);

    // Initialize with the original pictures
    useEffect(() => {
        if (data?.fields?.pictures) {
            setDuplicatedPictures(data.fields.pictures);
            const firstImageUrl = data.fields.pictures[0]?.fields?.file?.url;
            setActiveImage(firstImageUrl ? getImageUrl(firstImageUrl) : '');
            setActiveIndex(0);
        }
    }, [data?.fields?.pictures]);

    // Calculate the original index based on the duplicated array index
    const getOriginalIndex = useCallback((duplicatedIndex) => {
        const originalLength = dataRef.current?.fields?.pictures?.length || 0;
        return duplicatedIndex % originalLength;
    }, []);

    const handleScroll = useCallback(() => {

        const currentScrollY = window.scrollY;
        const direction = currentScrollY > lastScrollY.current ? 'down' : 'up';
        lastScrollY.current = currentScrollY;

        // Check if we're near the top or bottom
        const nearBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 250;
        const nearTop = window.scrollY <= 250;

        if (nearBottom && direction === 'down') {
            // When near bottom and scrolling down, duplicate the pictures
            setDuplicatedPictures(prev => [...prev, ...dataRef.current.fields.pictures]);
        } else if (nearTop && direction === 'up') {
            // When near top and scrolling up, duplicate the pictures at the beginning
            setDuplicatedPictures(prev => [...dataRef.current.fields.pictures, ...prev]);
        }

        // Find the image whose top is closest to (but not above) the top of the viewport
        let topIndex = -1;
        let topDistance = Infinity;

        imageRefs.current.forEach((img, index) => {
            if (img) {
                const rect = img.getBoundingClientRect();
                // Only consider images whose top is at or below the top of the viewport
                if (rect.top >= 0 && rect.top < topDistance) {
                    topDistance = rect.top;
                    topIndex = index;
                }
            }
        });

        if (topIndex !== -1 && duplicatedPicturesRef.current[topIndex]) {
            const newUrl = getImageUrl(duplicatedPicturesRef.current[topIndex].fields.file.url);
            if (newUrl !== activeImageRef.current) {
                setActiveImage(newUrl);
            }
            setActiveIndex(getOriginalIndex(topIndex));
        }
    }, [getOriginalIndex]);

    useEffect(() => {
        window.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll();
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [handleScroll]);

    return (
        <motion.div
            className="test-scroll-infinite-data-container"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1 }}
        >
            <div className="container">
                <div className="picture-number">
                    <p>{Math.max(1, activeIndex + 1)}</p>
                </div>

                {!(yearsIsVisible || monthsIsVisible || keywordsIsVisible) && (
                    <div className="current-month">
                        <img src={data.fields.date} alt="month" />
                    </div>
                )}


                {!(yearsIsVisible || monthsIsVisible || keywordsIsVisible) && (
                    <div className="month-title">

                        <img src={data.fields.titreManuscrit.fields.file.url} alt="month" />

                    </div>
                )}

                <div className="fixed-image-viewer">
                    <img src={activeImage} alt="Currently visible" loading="lazy" />

                </div>

                <div className="button-container">
                    <div className="button-previous-month">
                        {nextMonth && (
                            <Link
                                to={`/${nextMonth.fields.url}`}
                                onClick={() => {
                                    setYearsIsVisible(false);
                                    setMonthsIsVisible(false);
                                    setKeywordsIsVisible(false);
                                }}
                            >
                                <img src="./../content/handwriting/arrow_left.png" alt="previous" />
                            </Link>
                        )}
                    </div>

                    <div className="button-next-month">
                        {previousMonth && (
                            <Link
                                to={`/${previousMonth.fields.url}`}
                                onClick={() => {
                                    setYearsIsVisible(false);
                                    setMonthsIsVisible(false);
                                    setKeywordsIsVisible(false);
                                }}
                            >
                                <img src="./../content/handwriting/arrow_right.png" alt="next" />
                            </Link>
                        )}
                    </div>
                </div>

                <div className="scroll-container" ref={containerRef}>
                    {duplicatedPictures.map((picture, index) => (
                        <img
                            key={`${picture.fields.file.url}-${index}`}
                            src={getImageUrl(picture.fields.file.url)}
                            data-index={index}
                            ref={(el) => (imageRefs.current[index] = el)}
                            alt="Timothée Lambrecq photography"
                            className="scroll-image"
                        />
                    ))}
                </div>
            </div>
        </motion.div>
    );
}

export default TestScrollInfiniteData;
